/**
 * priceManagementService.js
 * Motor de precios para el módulo /precios
 * Fórmula: PVP = Coste / (1 - margen)   [margen SOBRE PVP]
 */

import { tiendaDb } from '../lib/supabase';

// ─── FÓRMULAS ────────────────────────────────────────────────────────────────

/** PVP teórico sin redondeo. margen en decimal (0.35 = 35%) */
export const calcPvpTeorico = (costeReal, margen) => {
  if (!costeReal || costeReal <= 0) return null;
  if (!margen || margen <= 0 || margen >= 1) return null;
  return costeReal / (1 - margen);
};

/** Margen real sobre PVP. Devuelve % (35.44) o null */
export const calcMargenReal = (pvp, coste) => {
  if (!pvp || pvp <= 0 || !coste || coste <= 0) return null;
  return ((pvp - coste) / pvp) * 100;
};

/** Coste con impuestos = precio_compra * (1 + iva/100 + recargo/100) */
export const calcCosteConImpuestos = (precioCompra, iva = 10, recargo = 1.4) => {
  if (!precioCompra || precioCompra <= 0) return null;
  return precioCompra * (1 + iva / 100 + recargo / 100);
};

/** Aplicar regla de redondeo psicológico */
export const aplicarRedondeo = (precio, regla) => {
  if (!precio || precio <= 0) return precio;
  const base = Math.floor(precio);
  switch (regla) {
    case 'TERMINAR_99': return base + 0.99;
    case 'TERMINAR_95': return base + 0.95;
    case 'TERMINAR_90': return base + 0.90;
    case 'MULTIPLO_05': return Math.ceil(precio / 0.05) * 0.05;
    case 'MULTIPLO_10': return Math.ceil(precio / 0.10) * 0.10;
    default: return Math.round(precio * 100) / 100;
  }
};

/** PVP sugerido = pvpTeorico redondeado según regla de familia */
export const calcPvpSugerido = (costeReal, margenDecimal, reglaRedondeo = 'TERMINAR_99') => {
  const pvpTeorico = calcPvpTeorico(costeReal, margenDecimal);
  if (!pvpTeorico) return null;
  return aplicarRedondeo(pvpTeorico, reglaRedondeo);
};

/** Semáforo de precio */
export const calcSemaforo = (margenReal, margenMinimo = 20, margenObjetivo = 35, margenPremium = 50) => {
  if (margenReal === null || margenReal === undefined) return 'GRIS';
  if (margenReal < 0 || margenReal < margenMinimo) return 'ROJO';
  if (margenReal < margenObjetivo) return 'AMARILLO';
  if (margenReal >= margenPremium) return 'MORADO';
  return 'VERDE';
};

export const SEMAFORO_COLORS = {
  ROJO:    { bg: 'rgba(239,68,68,0.15)',   border: 'rgba(239,68,68,0.4)',   text: '#EF4444',  label: 'Margen bajo mínimo' },
  AMARILLO:{ bg: 'rgba(251,191,36,0.15)',  border: 'rgba(251,191,36,0.4)',  text: '#FBBF24',  label: 'Margen inferior objetivo' },
  VERDE:   { bg: 'rgba(52,211,153,0.15)',  border: 'rgba(52,211,153,0.4)',  text: '#34D399',  label: 'Margen correcto' },
  MORADO:  { bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.4)', text: '#A78BFA',  label: 'Margen premium' },
  GRIS:    { bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)', text: '#6B7280',  label: 'Sin datos' },
};

// ─── BÚSQUEDA DE PRODUCTO ─────────────────────────────────────────────────────

const normalizarProducto = (p) => {
  const coste = p?.coste_con_impuestos || p?.precio_compra || 0;
  const pvpActual = p?.pvp_venta || 0;
  const margenObj = (p?.margen_objetivo || p?.familia?.margen_objetivo || 35) / 100;
  const reglaRedondeo = p?.familia?.regla_redondeo || 'TERMINAR_99';
  const iva = p?.tipo_iva || p?.familia?.iva_defecto || 10;
  const recargo = p?.recargo_equivalencia_pct || p?.recargo_equivalencia || p?.familia?.recargo_defecto || 1.4;

  const pvpTeorico = calcPvpTeorico(coste, margenObj);
  const pvpSugerido = pvpTeorico ? aplicarRedondeo(pvpTeorico, reglaRedondeo) : null;
  const margenReal = calcMargenReal(pvpActual, coste);

  return {
    ...p,
    familiaId: p?.familia_id,
    familiaNombre: p?.familia?.nombre || '—',
    familiaMargenObjetivo: p?.familia?.margen_objetivo,
    familiaReglaRedondeo: reglaRedondeo,
    proveedorNombre: p?.proveedor?.nombre || '—',
    stockActual: p?.inventario?.[0]?.stock_actual ?? p?.inventario?.stock_actual ?? 0,
    ultimaCompra: p?.inventario?.[0]?.ultima_actualizacion || p?.inventario?.ultima_actualizacion || null,
    costeReal: coste,
    iva,
    recargo,
    pvpActual,
    pvpTeorico,
    pvpSugerido,
    margenObjetivo: (p?.margen_objetivo || p?.familia?.margen_objetivo || 35),
    margenReal,
  };
};

export const buscarProducto = async (query, tipo = 'nombre') => {
  if (!query?.trim()) return [];
  let q = tiendaDb?.from('productos')?.select(`
      id, codigo_interno, ean, plu, nombre, descripcion,
      familia_id, proveedor_principal_id,
      tipo_iva, recargo_equivalencia, recargo_equivalencia_pct,
      precio_compra, precio_compra_con_iva, coste_con_impuestos,
      pvp_venta, pvp_sugerido, pvp_teorico,
      margen_objetivo, margen_real, bloqueo_precio, activo,
      codigo_epelsa, updated_at,
      familia:familias_producto(id, nombre, margen_objetivo, regla_redondeo, iva_defecto, recargo_defecto),
      proveedor:proveedores(id, nombre),
      inventario:inventario_actual(stock_actual, stock_disponible, ultimo_coste, ultima_actualizacion)
    `)?.eq('activo', true)?.limit(20);

  switch (tipo) {
    case 'ean':
      q = q?.ilike('ean', `%${query}%`);
      break;
    case 'plu':
      q = q?.ilike('plu', `%${query}%`);
      break;
    case 'codigo':
      q = q?.or(`codigo_interno.ilike.%${query}%,codigo_epelsa.ilike.%${query}%`);
      break;
    default:
      q = q?.ilike('nombre', `%${query}%`);
  }

  const { data, error } = await q?.order('nombre');
  if (error) throw error;
  return (data || [])?.map(normalizarProducto);
};

export const getProductoById = async (id) => {
  const { data, error } = await tiendaDb?.from('productos')?.select(`
      id, codigo_interno, ean, plu, nombre, descripcion,
      familia_id, proveedor_principal_id,
      tipo_iva, recargo_equivalencia, recargo_equivalencia_pct,
      precio_compra, precio_compra_con_iva, coste_con_impuestos,
      pvp_venta, pvp_sugerido, pvp_teorico,
      margen_objetivo, margen_real, bloqueo_precio, activo,
      codigo_epelsa, updated_at,
      familia:familias_producto(id, nombre, margen_objetivo, regla_redondeo, iva_defecto, recargo_defecto),
      proveedor:proveedores(id, nombre),
      inventario:inventario_actual(stock_actual, stock_disponible, ultimo_coste, ultima_actualizacion)
    `)?.eq('id', id)?.single();
  if (error) throw error;
  return normalizarProducto(data);
};

// ─── GUARDAR PRECIO ───────────────────────────────────────────────────────────

export const guardarPrecio = async (productoId, nuevoPvp, datos) => {
  const { costeReal, margenNuevo, margenAnterior, pvpAnterior, usuario = 'ERP', origen = 'MANUAL' } = datos;

  // 1. Actualizar producto
  const { error: updateError } = await tiendaDb?.from('productos')?.update({
      pvp_venta: nuevoPvp,
      pvp_sugerido: datos?.pvpSugerido || null,
      pvp_teorico: datos?.pvpTeorico || null,
      margen_real: margenNuevo,
      updated_at: new Date()?.toISOString(),
    })?.eq('id', productoId);
  if (updateError) throw updateError;

  // 2. Guardar histórico de cambio PVP
  const { data: histEntry, error: histError } = await tiendaDb?.from('historico_cambios_pvp')?.insert({
      producto_id: productoId,
      pvp_anterior: pvpAnterior,
      pvp_nuevo: nuevoPvp,
      coste_real: costeReal,
      margen_anterior: margenAnterior,
      margen_nuevo: margenNuevo,
      usuario,
      origen,
      estado_sync_epelsa: 'PENDIENTE',
    })?.select()?.single();
  if (histError) throw histError;

  // 3. Añadir a cola de sync Epelsa
  const { data: colaEntry, error: colaError } = await tiendaDb?.from('cola_sync_precios_epelsa')?.insert({
      producto_id: productoId,
      codigo_epelsa: datos?.codigoEpelsa || null,
      pvp_anterior: pvpAnterior,
      pvp_nuevo: nuevoPvp,
      coste_real: costeReal,
      margen_aplicado: margenNuevo,
      origen,
      usuario,
      estado: 'PENDIENTE',
    })?.select()?.single();

  // Actualizar histórico con referencia a cola
  if (!colaError && colaEntry) {
    await tiendaDb?.from('historico_cambios_pvp')?.update({ cola_sync_id: colaEntry?.id })?.eq('id', histEntry?.id);
  }

  return { histEntry, colaEntry, estadoSync: 'PENDIENTE' };
};

// ─── HISTÓRICO DE PRECIOS ─────────────────────────────────────────────────────

export const getHistoricoPvp = async (productoId, limit = 20) => {
  const { data, error } = await tiendaDb?.from('historico_cambios_pvp')?.select('*')?.eq('producto_id', productoId)?.order('created_at', { ascending: false })?.limit(limit);
  if (error) throw error;
  return data || [];
};

export const getAuditoriaPrecios = async (limit = 50) => {
  const { data, error } = await tiendaDb?.from('historico_cambios_pvp')?.select(`
      *,
      producto:productos(nombre, codigo_interno, ean)
    `)?.order('created_at', { ascending: false })?.limit(limit);
  if (error) throw error;
  return data || [];
};

// ─── DASHBOARD KPIs ───────────────────────────────────────────────────────────

export const getDashboardKpis = async () => {
  const { data: productos, error } = await tiendaDb?.from('productos')?.select(`
      id, precio_compra, coste_con_impuestos, pvp_venta,
      margen_objetivo, margen_real, proveedor_principal_id,
      inventario:inventario_actual(stock_actual)
    `)?.eq('activo', true);

  if (error) throw error;

  const { count: pendientesSync } = await tiendaDb?.from('cola_sync_precios_epelsa')?.select('id', { count: 'exact', head: true })?.eq('estado', 'PENDIENTE');

  let margenBajo = 0, pvpBajoCoste = 0, sinCoste = 0, sinProveedor = 0, stockNegativo = 0, precioRevisar = 0;

  (productos || [])?.forEach(p => {
    const coste = p?.coste_con_impuestos || p?.precio_compra || 0;
    const pvp = p?.pvp_venta || 0;
    const margenObj = p?.margen_objetivo || 35;
    const stock = p?.inventario?.[0]?.stock_actual ?? 0;

    if (!coste || coste <= 0) sinCoste++;
    if (!p?.proveedor_principal_id) sinProveedor++;
    if (stock < 0) stockNegativo++;
    if (coste > 0 && pvp > 0 && pvp < coste) pvpBajoCoste++;
    if (coste > 0 && pvp > 0) {
      const mr = ((pvp - coste) / pvp) * 100;
      if (mr < margenObj * 0.85) { margenBajo++; precioRevisar++; }
    }
  });

  return {
    total: productos?.length || 0,
    sinCoste,
    sinProveedor,
    stockNegativo,
    pvpBajoCoste,
    margenBajo,
    pendientesSync: pendientesSync || 0,
    precioRevisar,
  };
};

// ─── ACTUALIZACIÓN MASIVA ─────────────────────────────────────────────────────

export const getProductosFiltrados = async (filtros = {}) => {
  let q = tiendaDb?.from('productos')?.select(`
      id, codigo_interno, ean, nombre,
      familia_id, proveedor_principal_id,
      tipo_iva, recargo_equivalencia_pct,
      precio_compra, coste_con_impuestos,
      pvp_venta, pvp_sugerido, margen_objetivo, margen_real,
      bloqueo_precio, activo,
      familia:familias_producto(id, nombre, margen_objetivo, regla_redondeo),
      proveedor:proveedores(id, nombre),
      inventario:inventario_actual(stock_actual)
    `)?.eq('activo', true);

  if (filtros?.familia_id) q = q?.eq('familia_id', filtros?.familia_id);
  if (filtros?.proveedor_id) q = q?.eq('proveedor_principal_id', filtros?.proveedor_id);

  const { data, error } = await q?.order('nombre')?.limit(200);
  if (error) throw error;

  let result = (data || [])?.map(normalizarProducto);

  if (filtros?.sinCoste) result = result?.filter(p => !p?.costeReal || p?.costeReal <= 0);
  if (filtros?.margenBajo) result = result?.filter(p => {
    if (!p?.margenReal || !p?.margenObjetivo) return false;
    return p?.margenReal < p?.margenObjetivo * 0.85;
  });
  if (filtros?.pvpBajoCoste) result = result?.filter(p => p?.costeReal > 0 && p?.pvpActual > 0 && p?.pvpActual < p?.costeReal);

  return result;
};

export const aplicarCambioMasivo = async (ids, cambios, usuario = 'ERP') => {
  const errores = [];
  for (const id of ids) {
    try {
      const producto = await getProductoById(id);
      const pvpAnterior = producto?.pvpActual;
      const costeReal = producto?.costeReal;

      let nuevoPvp = pvpAnterior;
      if (cambios?.margenObjetivo && costeReal > 0) {
        const margenDec = cambios?.margenObjetivo / 100;
        const pvpTeorico = calcPvpTeorico(costeReal, margenDec);
        nuevoPvp = aplicarRedondeo(pvpTeorico, producto?.familiaReglaRedondeo || 'TERMINAR_99');
      }
      if (cambios?.nuevoPvp) nuevoPvp = cambios?.nuevoPvp;

      if (nuevoPvp && nuevoPvp !== pvpAnterior) {
        await guardarPrecio(id, nuevoPvp, {
          costeReal,
          margenNuevo: calcMargenReal(nuevoPvp, costeReal),
          margenAnterior: producto?.margenReal,
          pvpAnterior,
          pvpSugerido: nuevoPvp,
          pvpTeorico: calcPvpTeorico(costeReal, (cambios?.margenObjetivo || producto?.margenObjetivo) / 100),
          codigoEpelsa: producto?.codigo_epelsa,
          usuario,
          origen: 'MASIVO',
        });
      }

      // Actualizar otros campos si se piden
      const updates = {};
      if (cambios?.margenObjetivo) updates.margen_objetivo = cambios?.margenObjetivo;
      if (cambios?.bloqueo_precio !== undefined) updates.bloqueo_precio = cambios?.bloqueo_precio;
      if (Object.keys(updates)?.length > 0) {
        await tiendaDb?.from('productos')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', id);
      }
    } catch (e) {
      errores?.push({ id, error: e?.message });
    }
  }
  return { ok: ids?.length - errores?.length, errores };
};

// ─── FAMILIAS ────────────────────────────────────────────────────────────────

export const getFamilias = async () => {
  const { data, error } = await tiendaDb?.from('familias_producto')?.select('id, nombre, margen_objetivo, regla_redondeo, iva_defecto, recargo_defecto')?.eq('activo', true)?.order('nombre');
  if (error) throw error;
  return data || [];
};

export const getProveedores = async () => {
  const { data, error } = await tiendaDb?.from('proveedores')?.select('id, nombre')?.eq('activo', true)?.order('nombre');
  if (error) throw error;
  return data || [];
};
